import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Button, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './MarketingStrategy.css';

const PitchDeck: React.FC = () => {
  const theme = useTheme();
  const [totalPages, setTotalPages] = useState(0);
  const [pages, setPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [activeSlide, setActiveSlide] = useState(0);

  // Initialize PDF.js worker
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
    const loadPdf = async () => {
      try {
        const response = await fetch('/NEXTBASKET_Pitch_Deck.pdf');
        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        
        const loadingTask = getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        setTotalPages(pdf.numPages);
        
        const pagesPromises = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          await page.render({
            canvasContext: context!,
            viewport: viewport
          }).promise;
          
          pagesPromises.push(canvas.toDataURL());
        }
        
        const pageUrls = await Promise.all(pagesPromises);
        setPages(pageUrls);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        setLoading(false);
      }
    };
    
    loadPdf();
  }, [scale]); // Re-render when scale changes

  return (
    <Box sx={{ width: '100%', height: '100%', mb: 6, overflow: 'hidden' }}>
      <Typography
        variant="h3"
        component="h2"
        align="center"
        sx={{
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
          fontWeight: 700,
          mb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 70%, #1a4b80 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textTransform: 'uppercase',
          '&::after': {
            content: '""',
            display: 'block',
            width: '60px',
            height: '4px',
            backgroundColor: theme.palette.primary.main,
            margin: '16px auto 0',
            borderRadius: '2px'
          }
        }}
      >
        PITCH DECK
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          opacity: 0.8,
          mb: { xs: 1, sm: 2 }
        }}
      >
        20 slides available
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          textAlign: 'center',
          mb: 2,
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '& svg': {
            animation: 'slideHint 1.5s ease-in-out infinite',
            '@keyframes slideHint': {
              '0%, 100%': { transform: 'translateX(0)' },
              '50%': { transform: 'translateX(5px)' }
            }
          }
        }}
      >
        Swipe or use arrows for more content
        <ArrowRightIcon sx={{ fontSize: 17 }} />
      </Typography>

      <Box
        sx={{
          mt: { xs: 2, md: 4 },
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          border: '1px solid rgba(63, 160, 255, 0.3)',
          p: 0,
          height: { xs: '20vh', md: 'calc(100vh - 300px)' },
          minHeight: { xs: '400px', md: '400px' },
          overflow: 'hidden',
          mx: 'auto',
          
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.1) 0%, rgba(63, 160, 255, 0.05) 100%)',
            borderRadius: '16px',
            pointerEvents: 'none'
          }
        }}
      >
        {loading ? (
          <Typography
            sx={{
              color: theme.palette.primary.main,
              textAlign: 'center',
              mt: 4
            }}
          >
            Loading PDF...
          </Typography>
        ) : error ? (
          <Typography
            color="error"
            sx={{
              textAlign: 'center',
              mt: 4
            }}
          >
            {error}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
                display: 'flex',
                gap: 1,
                padding: '4px'
              }}
            >
              <IconButton
                onClick={() => setScale(prev => Math.max(prev - 0.25, 0.5))}
                disabled={scale <= 0.5}
                sx={{
                  width: 44,
                  height: 44,
                  color: '#50b5ff',
                  border: '1px solid rgba(80, 181, 255, 0.3)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    color: '#80cfff',
                    backgroundColor: 'transparent',
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.1)',
                      filter: 'drop-shadow(0 0 5px rgba(63, 160, 255, 0.5))'
                    }
                  },
                  '&:active': {
                    transform: 'translateY(1px)'
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.5rem',
                    transition: 'all 0.3s ease'
                  },
                  '&.Mui-disabled': { 
                    color: 'rgba(63, 160, 255, 0.3)',
                    borderColor: 'rgba(63, 160, 255, 0.1)'
                  }
                }}
              >
                <ZoomOutIcon />
              </IconButton>
              <IconButton
                onClick={() => setScale(prev => Math.min(prev + 0.25, 3))}
                disabled={scale >= 3}
                sx={{
                  width: 44,
                  height: 44,
                  color: '#50b5ff',
                  border: '1px solid rgba(80, 181, 255, 0.3)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    color: '#80cfff',
                    backgroundColor: 'transparent',
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.1)',
                      filter: 'drop-shadow(0 0 5px rgba(63, 160, 255, 0.5))'
                    }
                  },
                  '&:active': {
                    transform: 'translateY(1px)'
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.5rem',
                    transition: 'all 0.3s ease'
                  },
                  '&.Mui-disabled': { 
                    color: 'rgba(63, 160, 255, 0.3)',
                    borderColor: 'rgba(63, 160, 255, 0.1)'
                  }
                }}
              >
                <ZoomInIcon />
              </IconButton>
            </Box>
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              onSlideChange={(swiper: SwiperType) => setActiveSlide(swiper.activeIndex)}
              spaceBetween={0}
              centeredSlides={true}
              slidesPerView={1}
              style={{
                width: '100%',
                height: '100%',
                '--swiper-navigation-size': '24px',
                '--swiper-pagination-color': '#3FA0FF'
              } as any}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 0,
                  width: undefined
                }
              }}
              className="marketing-strategy-swiper"
            >
                {pages.map((pageUrl, index) => (
                  <SwiperSlide key={`page_${index + 1}`}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        p: { xs: 1, md: 0 }
                      }}
                    >
                      <img 
                        src={pageUrl} 
                        alt={`Page ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '12px',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          display: 'block',
                          transform: activeSlide === index ? `scale(${scale})` : 'scale(1)',
                          transformOrigin: 'center center',
                          transition: 'transform 0.2s ease-out'
                        }}
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
          </>
        )}

      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: { xs: 2, md: 4 } }}>

        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.primary',
            mb: 2
          }}
        >
          <br />
          Download our detailed Pitch Deck to learn more about NEXT BASKET
          <br /><br />
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            try {
              const link = document.createElement('a');
              link.href = '/NEXTBASKET_Pitch_Deck.pdf';
              link.download = 'NEXTBASKET_Pitch_Deck.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } catch (error) {
              console.error('Error downloading file:', error);
            }
          }}
          startIcon={<DownloadIcon />}
          sx={{
            py: { xs: 1.75, md: 1.75 },
            px: { xs: 4, md: 5 },
            minWidth: { xs: 'auto', md: '300px' },
            borderRadius: 15,
            textTransform: 'none',
            fontSize: { xs: 18, md: 17 },
            fontWeight: 600,
            letterSpacing: '0.02em',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: '#fff',
            border: '1px solid rgba(63, 160, 255, 0.3)',
            backdropFilter: 'blur(8px)',
            zIndex: theme.zIndex.modal + 1,
            '& .MuiSvgIcon-root': {
              color: '#fff',
              fontSize: '1.4rem',
              marginRight: '8px',
              transition: 'transform 0.3s ease'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
              transform: 'translateX(-100%) skewX(-15deg)',
              transition: 'transform 0.7s ease'
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: '0 8px 25px rgba(63, 160, 255, 0.4)',
              transform: 'translateY(-2px)',
              border: '1px solid rgba(63, 160, 255, 0.5)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1) rotate(-10deg)'
              },
              '&::before': {
                transform: 'translateX(100%) skewX(-15deg)'
              }
            },
            '&:active': {
              transform: 'translateY(1px)',
              boxShadow: '0 4px 15px rgba(63, 160, 255, 0.3)'
            }
          }}
        >
          Download PDF
        </Button>
        <br />
      </Box>
    </Box>
  );
};

export default PitchDeck;
